// Stockify購読プラン自動選択スクリプト（統合版）
// 目的：FULL PAYMENTプランを動的に検出して自動で有効化し、「Unknown」価格表示を防止
// 統合機能：plan-auto-select.js + stockify-auto-select.js + stockify-custom.js

// 実行済みフラグ
let planActivationCompleted = false;

// 価格監視用のインターバルID
let priceMonitorInterval = null;

// イベント発火を制御するフラグ
let eventDebounceTimer = null;
let lastSelectedPlanId = null;

// 初期価格キャッシュ
let initialPriceCache = null;

/**
 * Shopify標準の商品データから価格を取得する関数
 * data-selected-variantスクリプトタグから価格情報を抽出
 */
function getPriceFromShopifyData() {
    try {
        // data-selected-variantスクリプトタグから価格を取得
        const variantScript = document.querySelector('script[data-selected-variant]');
        if (variantScript) {
            const variantData = JSON.parse(variantScript.textContent);
            if (variantData && variantData.price) {
                // Shopifyの価格フォーマット（セント単位）を日本円に変換
                const priceInYen = variantData.price / 100;
                return `¥${priceInYen.toLocaleString('ja-JP')}`;
            }
        }
        
        // フォールバック：Shopify標準価格要素から取得
        const standardPriceElements = document.querySelectorAll('.price-item--regular, .price-item--sale, .price__regular .price-item, .price__sale .price-item');
        for (const elem of standardPriceElements) {
            const text = elem.textContent.trim();
            if (text && (text.includes('¥') || text.includes('$')) && !text.includes('Unknown')) {
                return text;
            }
        }
        
        // 最終フォールバック：価格コンテナから取得
        const priceContainers = document.querySelectorAll('.price__container');
        for (const container of priceContainers) {
            const text = container.textContent.trim();
            const priceMatch = text.match(/¥[\d,]+/);
            if (priceMatch) {
                return priceMatch[0];
            }
        }
        
        return null;
    } catch (error) {
        console.error('Shopify価格データ取得エラー:', error);
        return null;
    }
}

/**
 * 初期価格保護と事前価格設定
 */
function initializePriceProtection() {
    // Shopify標準価格を事前にキャッシュ
    const shopifyPrice = getPriceFromShopifyData();
    if (shopifyPrice) {
        initialPriceCache = shopifyPrice;
    }
    
    // 価格要素を事前に正しい価格で初期化
    const priceElements = document.querySelectorAll('.price--selling-plan, .selling-plan-price, .product__price');
    priceElements.forEach(elem => {
        const text = elem.textContent.trim();
        
        // 既にUnknownが表示されている場合は即座にShopify価格で置換
        if (text.includes('Unknown') || text.includes('Pre-order') || !text) {
            if (shopifyPrice) {
                elem.textContent = shopifyPrice;
                elem.style.visibility = 'visible';
                elem.style.display = 'block';
            }
        }
        
        // 正常な価格を保存
        if (text && !text.includes('Unknown') && (text.includes('¥') || text.includes('$'))) {
            initialPriceCache = text;
        }
    });
    
    // 価格を一時的に保持するマップ
    const priceMap = new Map();
    
    // Unknownテキストの置換を監視
    const observer = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
            if (mutation.type === 'characterData' || mutation.type === 'childList') {
                const target = mutation.target.nodeType === Node.TEXT_NODE ? mutation.target.parentElement : mutation.target;
                
                // 価格要素の場合
                if (target && (target.classList.contains('price--selling-plan') || 
                             target.classList.contains('selling-plan-price') || 
                             target.classList.contains('product__price') ||
                             target.closest('.price__container'))) {
                    
                    const text = target.textContent.trim();
                    
                    // 正常な価格をキャッシュ
                    if (text && !text.includes('Unknown') && (text.includes('¥') || text.includes('$'))) {
                        priceMap.set(target, text);
                        initialPriceCache = text; // グローバルキャッシュも更新
                    }
                    // Unknownが表示されたら即座に復元
                    else if (text.includes('Unknown') || text.includes('Pre-order') || !text) {
                        const cachedPrice = priceMap.get(target) || initialPriceCache || shopifyPrice;
                        if (cachedPrice) {
                            // 即座に修正
                            target.textContent = cachedPrice;
                            target.style.visibility = 'visible';
                            target.style.display = 'block';
                        }
                    }
                }
            }
        });
    });
    
    // 価格要素を監視
    priceElements.forEach(elem => {
        observer.observe(elem, {
            childList: true,
            characterData: true,
            subtree: true
        });
    });
    
    return observer;
}

// 価格保護を即座に開始（DOMContentLoaded前でも実行）
const priceProtectionObserver = initializePriceProtection();

// DOM読み込み前でも価格保護を実行
if (document.readyState === 'loading') {
    // ページ読み込み中にも価格チェックを実行
    const earlyPriceCheck = setInterval(() => {
        const shopifyPrice = getPriceFromShopifyData();
        if (shopifyPrice) {
            const priceElements = document.querySelectorAll('.price--selling-plan, .selling-plan-price, .product__price');
            priceElements.forEach(elem => {
                if (elem.textContent.includes('Unknown') || elem.textContent.includes('Pre-order') || !elem.textContent.trim()) {
                    elem.textContent = shopifyPrice;
                    elem.style.visibility = 'visible';
                    elem.style.display = 'block';
                }
            });
        }
    }, 50);
    
    // DOMContentLoaded後に停止
    document.addEventListener('DOMContentLoaded', () => {
        clearInterval(earlyPriceCheck);
    });
}

/**
 * FULL PAYMENTプランを動的に検出する関数
 * プラン名や価格から最適なプランを特定
 */
function findFullPaymentPlan(planElements) {
    let fullPaymentPlan = null;
    let maxPrice = 0;
    
    for (const planElement of planElements) {
        const planId = planElement.getAttribute('data-plan-id');
        if (!planId) continue;
        
        // プラン名やテキストからFULL PAYMENTを検出
        const planText = planElement.textContent.toLowerCase();
        const isFullPayment = planText.includes('full') || 
                            planText.includes('一括') || 
                            planText.includes('フル') || 
                            planText.includes('全額');
        
        if (isFullPayment) {
            return { element: planElement, planId: planId };
        }
        
        // フォールバック：最高価格のプランを検出
        const priceElement = planElement.querySelector('[data-price], .price, .selling-plan-price');
        if (priceElement) {
            const priceText = priceElement.textContent.replace(/[^\d]/g, '');
            const price = parseInt(priceText) || 0;
            if (price > maxPrice) {
                maxPrice = price;
                fullPaymentPlan = { element: planElement, planId: planId };
            }
        }
    }
    
    return fullPaymentPlan;
}

/**
 * プラン要素から価格データを抽出する関数（階層的フォールバック付き）
 * 1st: Stockify Shadow DOM価格 → 2nd: Shopify標準価格 → 3rd: JSON価格
 */
function extractPriceData(planElement) {
    // Stockify固有の価格要素を探す（Shadow DOM内も含む）
    let priceText = null;
    let compareText = null;
    
    // 方法1: data属性から直接価格を取得
    const priceAttr = planElement.getAttribute('data-price');
    const compareAttr = planElement.getAttribute('data-compare-price');
    
    if (priceAttr) {
        priceText = priceAttr;
    }
    
    // 方法2: 子要素から価格を検索
    if (!priceText) {
        const priceSelectors = [
            '.selling-plan-price',
            '.plan-price',
            '[data-price]',
            '.price',
            '.money'
        ];
        
        for (const selector of priceSelectors) {
            const element = planElement.querySelector(selector);
            if (element && element.textContent.trim()) {
                priceText = element.textContent.trim();
                break;
            }
        }
    }
    
    // 方法3: テキストコンテンツから価格を抽出
    if (!priceText) {
        const text = planElement.textContent;
        const priceMatch = text.match(/[¥$]\s*[\d,]+(\.\d{2})?/);
        if (priceMatch) {
            priceText = priceMatch[0];
        }
    }
    
    // 方法4: Stockifyのデフォルト価格フォーマットから取得
    if (!priceText || priceText === 'Unknown') {
        // プラン内のすべてのspan要素をチェック
        const spans = planElement.querySelectorAll('span');
        for (const span of spans) {
            const text = span.textContent.trim();
            if (text.includes('¥') || text.includes('$') || text.includes('円') || text.includes('JPY')) {
                priceText = text;
                break;
            }
        }
    }
    
    // 方法5: Shopify標準価格データからフォールバック
    if (!priceText || priceText === 'Unknown') {
        const shopifyPrice = getPriceFromShopifyData();
        if (shopifyPrice) {
            priceText = shopifyPrice;
        }
    }
    
    return {
        price: priceText || '価格確認中...',
        compareAtPrice: compareText || compareAttr || null
    };
}

/**
 * プランの自動有効化を実行する関数
 * Shadow DOM内のStockify要素を操作してプランを切り替える
 */
function autoActivatePlan() {
    // 既に実行済みの場合はスキップ
    if (planActivationCompleted) {
        return;
    }
    
    // Stockify Info-Messageの重複を先に削除
    removeDuplicateStockifyInfoMessages();
    // Stockify要素（Shadow DOMを含む）を検索
    const stockifyElements = document.querySelectorAll('stockify-selling-plans');
    
    stockifyElements.forEach(element => {
        // Shadow DOMが利用可能な場合
        if (element.shadowRoot) {
            const shadowPlans = element.shadowRoot.querySelectorAll('[data-plan-id]');
            
            if (shadowPlans.length > 0) {
                // FULL PAYMENTプランを動的に検出
                const fullPaymentPlan = findFullPaymentPlan(shadowPlans);
                
                if (fullPaymentPlan) {
                    // すべてのShadow DOM内プランからplan--activeクラスを削除
                    shadowPlans.forEach(shadowElement => {
                        shadowElement.classList.remove('plan--active');
                        const radio = shadowElement.querySelector('input[type="radio"]');
                        if (radio) {
                            radio.checked = false;
                        }
                    });
                    
                    // 検出されたFULL PAYMENTプランを有効化
                    fullPaymentPlan.element.classList.add('plan--active');
                    
                    // Shadow DOM内のラジオボタンを選択
                    const shadowRadio = fullPaymentPlan.element.querySelector('input[type="radio"]');
                    if (shadowRadio) {
                        shadowRadio.checked = true;
                        shadowRadio.click();
                        shadowRadio.dispatchEvent(new Event('change', { bubbles: true }));
                        shadowRadio.dispatchEvent(new Event('input', { bubbles: true }));
                        
                        // Stockifyの隠し入力フィールドを動的プランIDで更新
                        const planInputs = document.querySelectorAll('input[name="selling_plan"]');
                        planInputs.forEach(input => {
                            input.value = fullPaymentPlan.planId;
                        });
                        
                        // Stockify--Hiddenクラスを削除して価格表示を有効にする
                        document.querySelectorAll('.Stockify--Hidden').forEach(hiddenElement => {
                            hiddenElement.classList.remove('Stockify--Hidden');
                        });
                        
                        // prices--selling-planクラスを持つ要素を表示
                        document.querySelectorAll('.prices.prices--sale.prices--selling-plan').forEach(priceElement => {
                            priceElement.style.display = 'block';
                            priceElement.style.visibility = 'visible';
                        });
                        
                        // 価格データを即座に取得
                        const priceData = extractPriceData(fullPaymentPlan.element);
                        
                        // 現在の商品バリアントIDを取得
                        const variantId = document.querySelector('[data-variant-id]')?.getAttribute('data-variant-id') || 
                                        document.querySelector('input[name="id"]')?.value || 
                                        document.querySelector('[data-product-variant-id]')?.getAttribute('data-product-variant-id') ||
                                        'unknown';
                        
                        // 価格が取得できた場合は即座に表示
                        if (priceData.price !== 'Unknown') {
                            const priceDisplays = document.querySelectorAll('.price--selling-plan, .selling-plan-price, .product__price, .price__container .price');
                            priceDisplays.forEach(display => {
                                display.textContent = priceData.price;
                                display.style.visibility = 'visible';
                                display.style.display = 'block';
                            });
                        }
                        
                        // Stockifyに価格変更イベントを即座に送信
                        document.dispatchEvent(new CustomEvent('STOCKIFY_SELLING_PLAN_CHANGED', {
                            detail: { 
                                planId: fullPaymentPlan.planId,
                                variantId: variantId,
                                available: true,
                                price: priceData.price,
                                compareAtPrice: priceData.compareAtPrice,
                                limit: 0,
                                itemsSold: 0
                            },
                            bubbles: true
                        }));
                        
                        // Stockifyが価格を更新するのを待つ
                        setTimeout(() => {
                            // 再度価格データを取得
                            const updatedPriceData = extractPriceData(fullPaymentPlan.element);
                            
                            // 価格を再度更新（Stockifyの上書きに対応）
                            const priceDisplays = document.querySelectorAll('.price--selling-plan, .selling-plan-price, .product__price, .price__container .price');
                            priceDisplays.forEach(display => {
                                if (updatedPriceData.price !== 'Unknown') {
                                    display.textContent = updatedPriceData.price;
                                    display.style.visibility = 'visible';
                                    display.style.display = 'block';
                                }
                            });
                            
                            // 継続的な価格監視を開始
                            if (priceMonitorInterval) {
                                clearInterval(priceMonitorInterval);
                            }
                            
                            let retryCount = 0;
                            priceMonitorInterval = setInterval(() => {
                                retryCount++;
                                
                                // Stockifyのイベントを再送信して価格を強制更新
                                shadowRadio.click();
                                shadowRadio.dispatchEvent(new Event('change', { bubbles: true }));
                                
                                // 価格表示要素を再チェック
                                const currentPriceDisplays = document.querySelectorAll('.price--selling-plan, .selling-plan-price, .product__price, .price__container');
                                currentPriceDisplays.forEach(display => {
                                    const displayText = display.textContent;
                                    if (displayText.includes('Unknown') || displayText.includes('Pre-order') || !displayText.includes('¥')) {
                                        // Shadow DOM内から価格を再取得
                                        const shadowPriceElements = element.shadowRoot.querySelectorAll('[data-plan-id="' + fullPaymentPlan.planId + '"] span, [data-plan-id="' + fullPaymentPlan.planId + '"] .price');
                                        for (const elem of shadowPriceElements) {
                                            const text = elem.textContent.trim();
                                            if (text.includes('¥') || text.includes('$')) {
                                                display.textContent = text;
                                                display.style.visibility = 'visible';
                                                display.style.display = 'block';
                                                break;
                                            }
                                        }
                                        
                                        // それでも見つからない場合は通常DOMから探す
                                        if (!display.textContent.includes('¥')) {
                                            const updatedPriceData = extractPriceData(fullPaymentPlan.element);
                                            if (updatedPriceData.price !== 'Unknown') {
                                                display.textContent = updatedPriceData.price;
                                            }
                                        }
                                    }
                                });
                                
                                // 20回試行後は停止
                                if (retryCount > 20) {
                                    clearInterval(priceMonitorInterval);
                                    priceMonitorInterval = null;
                                }
                            }, 250);
                            
                            // 5秒後に監視を停止
                            setTimeout(() => {
                                if (priceMonitorInterval) {
                                    clearInterval(priceMonitorInterval);
                                    priceMonitorInterval = null;
                                }
                            }, 5000);
                        
                        // 実行完了フラグをセット
                        planActivationCompleted = true;
                        }, 500);
                    }
                }
            }
        }
    });
    
    // 通常のDOMでもチェック（フォールバック）
    const allDataPlanElements = document.querySelectorAll('[data-plan-id]');
    if (allDataPlanElements.length > 0) {
        // FULL PAYMENTプランを動的に検出
        const fullPaymentPlan = findFullPaymentPlan(allDataPlanElements);
        
        if (fullPaymentPlan) {
            allDataPlanElements.forEach(element => {
                element.classList.remove('plan--active');
                const radio = element.querySelector('input[type="radio"]');
                if (radio) {
                    radio.checked = false;
                }
            });
            
            // 検出されたFULL PAYMENTプランを有効化
            fullPaymentPlan.element.classList.add('plan--active');
            const radioInput = fullPaymentPlan.element.querySelector('input[type="radio"]');
            if (radioInput) {
                radioInput.checked = true;
                radioInput.click();
                radioInput.dispatchEvent(new Event('change', { bubbles: true }));
                radioInput.dispatchEvent(new Event('input', { bubbles: true }));
                
                // Stockifyの隠し入力フィールドを動的プランIDで更新
                const planInputs = document.querySelectorAll('input[name="selling_plan"]');
                planInputs.forEach(input => {
                    input.value = fullPaymentPlan.planId;
                });
                
                // 価格データを即座に取得
                const priceData = extractPriceData(fullPaymentPlan.element);
                
                // 現在の商品バリアントIDを取得
                const variantId = document.querySelector('[data-variant-id]')?.getAttribute('data-variant-id') || 
                                document.querySelector('input[name="id"]')?.value || 
                                document.querySelector('[data-product-variant-id]')?.getAttribute('data-product-variant-id') ||
                                'unknown';
                
                // 価格が取得できた場合は即座に表示（Unknownを防ぐ）
                if (priceData.price !== 'Unknown') {
                    const priceDisplays = document.querySelectorAll('.price--selling-plan, .selling-plan-price, .product__price, .price__container .price');
                    priceDisplays.forEach(display => {
                        display.textContent = priceData.price;
                        display.style.visibility = 'visible';
                        display.style.display = 'block';
                    });
                }
                
                // Stockifyに価格変更イベントを即座に送信
                document.dispatchEvent(new CustomEvent('STOCKIFY_SELLING_PLAN_CHANGED', {
                    detail: { 
                        planId: fullPaymentPlan.planId,
                        variantId: variantId,
                        available: true,
                        price: priceData.price,
                        compareAtPrice: priceData.compareAtPrice,
                        limit: 0,
                        itemsSold: 0
                    },
                    bubbles: true
                }));
                
                // Stockifyが価格を更新するのを待つ
                setTimeout(() => {
                    // 再度価格データを取得
                    const updatedPriceData = extractPriceData(fullPaymentPlan.element);
                    if (updatedPriceData.price !== 'Unknown') {
                        const priceDisplays = document.querySelectorAll('.price--selling-plan, .selling-plan-price, .product__price, .price__container .price');
                        priceDisplays.forEach(display => {
                            display.textContent = updatedPriceData.price;
                            display.style.visibility = 'visible';
                            display.style.display = 'block';
                        });
                    }
                    
                    // Stockifyの価格表示を強制的に更新
                    const updatePriceDisplays = () => {
                        const priceDisplays = document.querySelectorAll('.price--selling-plan, .selling-plan-price, .product__price, .price__container .price');
                        priceDisplays.forEach(display => {
                            if (priceData.price !== 'Unknown' && priceData.price) {
                                display.textContent = priceData.price;
                                display.style.visibility = 'visible';
                                display.style.display = 'block';
                            }
                        });
                    };
                    
                    updatePriceDisplays();
                    
                    // 価格監視を開始
                    if (priceMonitorInterval) {
                        clearInterval(priceMonitorInterval);
                    }
                    
                    let monitorCount = 0;
                    priceMonitorInterval = setInterval(() => {
                        monitorCount++;
                        
                        // プランのラジオボタンが選択されていることを確認
                        if (!radioInput.checked) {
                            radioInput.checked = true;
                            radioInput.click();
                        }
                        
                        // 価格を再取得して更新
                        const latestPriceData = extractPriceData(fullPaymentPlan.element);
                        if (latestPriceData.price !== 'Unknown') {
                            priceData.price = latestPriceData.price;
                            updatePriceDisplays();
                        }
                        
                        // 10秒後に停止
                        if (monitorCount > 40) {
                            clearInterval(priceMonitorInterval);
                            priceMonitorInterval = null;
                        }
                    }, 250);
                
                // 実行完了フラグをセット
                planActivationCompleted = true;
                }, 500);
            }
        }
    }
}

/**
 * DOM読み込み後の実行制御
 * Stockifyアプリが要素を生成するまでに時間がかかるため、
 * 複数のタイミングでプラン有効化を実行する
 */
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
        removeDuplicateStockifyInfoMessages();
        handlePreorderStockifyMessages();
        setTimeout(autoActivatePlan, 500);   // 0.5秒後
        setTimeout(autoActivatePlan, 1000);  // 1秒後
        setTimeout(autoActivatePlan, 2000);  // 2秒後
    });
} else {
    // DOMが既に読み込まれている場合も同様に実行
    removeDuplicateStockifyInfoMessages();
    handlePreorderStockifyMessages();
    setTimeout(autoActivatePlan, 500);
    setTimeout(autoActivatePlan, 1000);
    setTimeout(autoActivatePlan, 2000);
}

/**
 * DOM変更監視（MutationObserver）
 * Stockifyが動的に要素を生成した場合に自動で検知して実行
 * Stockify--Hiddenクラスの追加も監視して即座に削除
 */
const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
        // 新しい要素の追加を監視
        if (mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach(function(node) {
                if (node.nodeType === 1 && (
                    node.getAttribute && node.getAttribute('data-plan-id') ||
                    node.querySelector && node.querySelector('[data-plan-id]') ||
                    node.innerHTML && (node.innerHTML.includes('stockify') || node.innerHTML.includes('plan'))
                )) {
                    if (!planActivationCompleted) {
                        setTimeout(autoActivatePlan, 50);
                    }
                }
            });
        }
        
        // クラス変更を監視してStockify--Hiddenを削除
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const target = mutation.target;
            if (target.classList && target.classList.contains('Stockify--Hidden')) {
                target.classList.remove('Stockify--Hidden');
            }
        }
    });
});

// Stockify重複要素の削除とHiddenクラス管理専用Observer
const stockifyDuplicateManager = new MutationObserver(function() {
    // Stockify Info-Messageの重複削除
    removeDuplicateStockifyInfoMessages();
    
    // Stockify--Hiddenクラスを削除
    document.querySelectorAll('.Stockify--Hidden').forEach(element => {
        element.classList.remove('Stockify--Hidden');
    });
    
    // Preorder商品でのinfo-message表示制御
    handlePreorderStockifyMessages();
});

// Stockify Info-Messageの重複を削除する関数
function removeDuplicateStockifyInfoMessages() {
    const infoMessages = document.querySelectorAll('stockify-info-message');
    if (infoMessages.length > 1) {
        // 最初の要素以外を削除
        for (let i = 1; i < infoMessages.length; i++) {
            infoMessages[i].remove();
        }
    }
}

// Preorder商品での適切なinfo-message表示制御
function handlePreorderStockifyMessages() {
    const variantPicker = document.querySelector('variant-selects');
    const infoMessages = document.querySelectorAll('stockify-info-message');
    
    if (infoMessages.length === 0) return;
    
    // Preorder商品かどうかを判定
    const isPreorderProduct = checkIfPreorderProduct();
    
    infoMessages.forEach(infoMessage => {
        if (!variantPicker) {
            // サイズ選択がない商品でPreorder商品の場合、info-messageを表示
            if (isPreorderProduct) {
                infoMessage.style.display = 'block';
                infoMessage.style.visibility = 'visible';
            }
        } else {
            // サイズ選択がある商品では、重複削除後に1つだけ表示
            if (isPreorderProduct) {
                infoMessage.style.display = 'block';
                infoMessage.style.visibility = 'visible';
            }
        }
    });
}

// Preorder商品判定の詳細ロジック
function checkIfPreorderProduct() {
    // 複数の方法でPreorder商品を検出
    const productInfo = document.querySelector('product-info');
    const productTitle = document.querySelector('.product__title');
    const productDescription = document.querySelector('.product__description');
    
    // 1. データ属性やメタフィールドでの判定
    if (productInfo?.dataset?.isPreorder === 'true' || 
        document.querySelector('[data-product-preorder]')) {
        return true;
    }
    
    // 2. 商品タイトルでの判定
    if (productTitle?.textContent?.toLowerCase().includes('preorder') ||
        productTitle?.textContent?.toLowerCase().includes('pre-order') ||
        productTitle?.textContent?.toLowerCase().includes('予約')) {
        return true;
    }
    
    // 3. 商品説明での判定
    if (productDescription?.textContent?.toLowerCase().includes('preorder') ||
        productDescription?.textContent?.toLowerCase().includes('pre-order') ||
        productDescription?.textContent?.toLowerCase().includes('予約注文')) {
        return true;
    }
    
    // 4. 商品タグでの判定
    const productJson = document.querySelector('[data-product]');
    if (productJson) {
        try {
            const product = JSON.parse(productJson.textContent);
            if (product.tags && (
                product.tags.includes('preorder') ||
                product.tags.includes('pre-order') ||
                product.tags.includes('予約')
            )) {
                return true;
            }
        } catch (e) {
            // JSONパースエラーは無視
        }
    }
    
    return false;
}

// DOM監視を開始
observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class']
});

// Stockify管理専用監視を開始
stockifyDuplicateManager.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class']
});

// グローバルテスト関数
window.testPlanActivation = autoActivatePlan;